import { GoalFilterDocument } from '@griply/types';
import { useGoalControls } from 'features/goals';
import React, { useMemo } from 'react';
import { DeleteGoalsProvider } from 'shared/contexts/delete-goals';
import { UpdateGoal } from 'shared/types/goal';
import { ID } from 'shared/types/id';
import { GoalGroupBy, GroupByMode } from 'shared/types/sorting-mode';

import { LifeAreaGroupingGoalList } from './components/life-area-grouping-goal-list';
import { NoGroupingGoalList } from './components/no-grouping-goal-list';
import { ContextMenuProvider } from './context-menu-context';
import { useGoalsByFilterDocument } from './hooks/use-goals-by-filter-document';
import { ContextMenuOptions, GroupingGoalListProps } from './types';

const groupByLists: Record<GoalGroupBy, React.FC<GroupingGoalListProps>> = {
  [GroupByMode.LifeArea]: LifeAreaGroupingGoalList,
  [GroupByMode.None]: NoGroupingGoalList,
  [GroupByMode.StartDate]: NoGroupingGoalList,
  [GroupByMode.Deadline]: NoGroupingGoalList,
};

export type GoalFilterListProps = {
  filterDocument: GoalFilterDocument;
  customOrder?: ID[];
  onSectionCollapse?: (collapsedSections: ID[]) => void;
  scrollElement?: React.RefObject<HTMLDivElement>;
  hideLifeArea?: boolean;
  hideArchived?: boolean;
  hideCompleted?: boolean;
  onReorder?: (
    updatedSections: { id: ID; goalSorting: ID[] }[],
    updatedGoals: UpdateGoal[],
  ) => void;
  isMultiSelectModifierPressed?: boolean;
  excludedContextMenuOptions?: ContextMenuOptions[];
};

const GoalFilterListContent: React.FC<GoalFilterListProps> = ({
  filterDocument,
  customOrder,
  scrollElement,
  hideLifeArea,
  hideArchived,
  hideCompleted,
  onSectionCollapse,
  onReorder,
  isMultiSelectModifierPressed,
  excludedContextMenuOptions,
}) => {
  // fetch the needed data
  const goals = useGoalsByFilterDocument(filterDocument, { customOrder });

  // Connect controls if not provided
  const { onUpdateGoalProgress } = useGoalControls(goals);

  // GroupBy
  const groupMode = filterDocument?.groupByMode ?? GroupByMode.LifeArea;
  const GroupedList = groupByLists[groupMode] ?? groupByLists[GroupByMode.None];

  // Collapsed State
  const collapsedSections = useMemo(
    () => filterDocument?.collapsedSections ?? [],
    [filterDocument?.collapsedSections],
  );

  return (
    <GroupedList
      goals={goals}
      collapsedGoals={collapsedSections}
      onGoalCollapse={onSectionCollapse}
      scrollElement={scrollElement}
      onReorder={onReorder}
      onUpdateProgress={onUpdateGoalProgress}
      hideLifeArea={hideLifeArea}
      hideArchived={hideArchived}
      hideCompleted={hideCompleted}
      isMultiSelectModifierPressed={isMultiSelectModifierPressed}
      excludedContextMenuOptions={excludedContextMenuOptions}
    />
  );
};

export const GoalFilterList: React.FC<GoalFilterListProps> = (props) => (
  <ContextMenuProvider>
    <DeleteGoalsProvider>
      <GoalFilterListContent {...props} />
    </DeleteGoalsProvider>
  </ContextMenuProvider>
);
